// Keep this boundary free of URLs, filenames, form values and error messages.
let clientPromise;
let userId = null;
let page = '/';
let queue = Promise.resolve();

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const getClient = () => {
  if (process.env.REACT_APP_ANALYTICS_ENABLED !== 'true' ||
      !Object.values(config).every(Boolean) || typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  if (!clientPromise) {
    clientPromise = Promise.all([import('firebase/app'), import('firebase/analytics')])
      .then(async ([appSdk, sdk]) => {
        if (!(await sdk.isSupported())) return null;
        const app = appSdk.getApps().find(item => item.name === 'activity-analytics') ||
          appSdk.initializeApp(config, 'activity-analytics');
        const analytics = sdk.initializeAnalytics(app, { config: {
          send_page_view: false,
          page_location: `${window.location.origin}${page}`,
          page_referrer: '',
          page_title: page,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
        } });
        return { sdk, analytics };
      }).catch(() => null);
  }
  return clientPromise;
};

// Serialize operations so a slow SDK load cannot attribute old events to a new user.
const enqueue = (operation) => {
  queue = queue.then(async () => {
    const client = await getClient();
    if (client) operation(client);
  }).catch(() => {});
  return queue;
};

export const identifyAnalyticsUser = (id) => {
  userId = id ? String(id) : null;
  const snapshot = userId;
  return enqueue(({ sdk, analytics }) => sdk.setUserId(analytics, snapshot));
};

const allowedEvents = new Set([
  'login', 'sign_up', 'logout', 'transfer_created', 'transfer_creation_failed',
  'selection_upload_started', 'selection_upload_completed', 'selection_upload_failed',
  'gallery_created', 'selection_link_created', 'gallery_selection_submitted',
  'download_requested', 'landing_link_clicked', 'landing_demo_interacted',
]);

export const trackActivity = (name, properties = {}) => {
  if (!allowedEvents.has(name)) return Promise.resolve();
  const eventProperties = {};
  for (const key of ['file_count', 'total_size_bytes']) {
    if (Number.isFinite(properties[key]) && properties[key] >= 0) eventProperties[key] = properties[key];
  }
  if (name === 'landing_link_clicked') {
    if (['signup', 'login', 'book_demo', 'how_it_works', 'contact', 'security',
      'privacy', 'terms', 'home', 'finance'].includes(properties.action)) {
      eventProperties.action = properties.action;
    }
    if (['header', 'hero', 'gallery_demo', 'finance', 'bottom_cta', 'footer'].includes(properties.placement)) {
      eventProperties.placement = properties.placement;
    }
  }
  const snapshot = { userId, page };
  return enqueue(({ sdk, analytics }) => {
    sdk.setUserId(analytics, snapshot.userId);
    sdk.logEvent(analytics, name, {
      ...eventProperties, page_location: `${window.location.origin}${snapshot.page}`,
      page_title: snapshot.page, page_referrer: '',
    });
  });
};

export const trackPageView = (routeTemplate) => {
  page = routeTemplate;
  const snapshot = { userId, page };
  return enqueue(({ sdk, analytics }) => {
    sdk.setUserId(analytics, snapshot.userId);
    const parameters = {
      page_location: `${window.location.origin}${snapshot.page}`,
      page_title: snapshot.page, page_referrer: '',
    };
    sdk.setDefaultEventParameters(parameters);
    sdk.logEvent(analytics, 'page_view', parameters);
  });
};
